import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import {
    NotificationToastService,
    NotificationToastView
} from '@/services/notification-toast.service';
import { NotificationApiService } from '@/services/notification-api.service';
import { NotificationInboxService } from '@/services/notification-inbox.service';
import { ProductUiService } from '@/services/product-ui.service';
import { OrgUsersService } from '@/services/org-users.service';
import { OrgService } from '@/services/org.service';
import { PostDto } from '@/helper/dto/cmp/post.dto';
import { formatPostIdentifier, orgPostKeyPrefix } from '@/helper/post-identifier';
import { EmojiTextComponent } from '@/shared/editor/emoji-text.component';

@Component({
    selector: 'app-notification-toast-host',
    standalone: true,
    imports: [CommonModule, TranslatePipe, EmojiTextComponent],
    template: `
        <div class="toast-stack" aria-live="polite">
            @for (item of toastService.toasts(); track item.toastId) {
                <button
                    type="button"
                    class="board-card toast-card"
                    (click)="onToastClick(item)"
                >
                    @if (postId(item); as identifier) {
                        <div class="id">{{ identifier }}</div>
                    }
                    <div class="title"><app-emoji-text [text]="headline(item)" /></div>
                    @if (notificationText(item)) {
                        <div class="notification-text">{{ notificationText(item) }}</div>
                    }
                    @if (item.post) {
                        <div class="board-card-meta">
                            <span class="type-badge" [attr.data-type]="item.post.type">{{
                                'cmp.post.types.' + item.post.type | translate
                            }}</span>
                            @if (item.post.deadlineAt) {
                                <span class="meta">{{ item.post.deadlineAt | date: 'dd MMM' }}</span>
                            }
                            @for (label of (item.labels || []).slice(0, 2); track label._id || label.name) {
                                <span
                                    class="label-pill"
                                    [style.--pill-color]="label.color || '#94a3b8'"
                                    [title]="label.name"
                                >
                                    <span class="pill-dot"></span>
                                    @if (label.icon) {
                                        <i class="pi pi-{{ label.icon }}"></i>
                                    }
                                    <span class="pill-label">{{ label.name }}</span>
                                </span>
                            }
                            @if ((item.post.assigneeIds || []).length) {
                                <span class="assignees">
                                    @for (a of (item.post.assigneeIds || []).slice(0, 2); track assigneeId(a)) {
                                        <span class="avatar" [title]="orgUsers.labelFor(a)">{{
                                            orgUsers.initialsFor(a)
                                        }}</span>
                                    }
                                </span>
                            }
                        </div>
                    }
                </button>
            }
        </div>
    `,
    styles: [
        `
            :host {
                display: contents;
            }
            .toast-stack {
                position: fixed;
                right: 1.25rem;
                bottom: 1.25rem;
                z-index: 1100;
                display: flex;
                flex-direction: column-reverse;
                align-items: stretch;
                gap: 0.5rem;
                width: min(320px, calc(100vw - 2.5rem));
                pointer-events: none;
            }
            .toast-card {
                pointer-events: auto;
                width: 100%;
                text-align: left;
                animation: toast-in 0.22s ease-out;
            }
            .toast-card:focus-visible {
                outline: 2px solid color-mix(in srgb, var(--cmp-accent, #5e6ad2) 55%, transparent);
                outline-offset: 2px;
            }
            .board-card {
                border: 1px solid var(--cmp-sidebar-border, #e8e8ea);
                border-radius: 8px;
                padding: 0.55rem 0.65rem;
                background: var(--p-surface-0, #fff);
                cursor: pointer;
                margin: 0;
                box-shadow:
                    0 4px 16px rgba(15, 23, 42, 0.08),
                    0 1px 3px rgba(15, 23, 42, 0.06);
            }
            .board-card:hover {
                background: var(--cmp-row-hover, rgba(0, 0, 0, 0.03));
            }
            .id {
                font-size: 11px;
                color: var(--cmp-muted, #6b6f76);
                margin-bottom: 0.15rem;
            }
            .title {
                font-size: 13px;
                font-weight: 500;
                color: var(--cmp-text, #222326);
                line-height: 1.35;
            }
            .notification-text {
                margin-top: 0.25rem;
                font-size: 12px;
                line-height: 1.35;
                color: var(--cmp-muted, #6b6f76);
            }
            .board-card-meta {
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                gap: 0.35rem;
                margin-top: 0.4rem;
            }
            .meta {
                font-size: 11px;
                color: var(--cmp-muted, #6b6f76);
            }
            .type-badge {
                font-size: 11px;
                padding: 0.1rem 0.4rem;
                border-radius: 999px;
                background: var(--cmp-row-hover, #eef0f3);
                color: var(--cmp-muted, #5c6169);
            }
            .label-pill {
                display: inline-flex;
                align-items: center;
                gap: 0.25rem;
                padding: 0.1rem 0.4rem 0.1rem 0.35rem;
                border-radius: 999px;
                border: 1px solid var(--cmp-sidebar-border, #e4e5e9);
                background: var(--p-surface-0, #fff);
                font-size: 11px;
                font-weight: 500;
                color: var(--cmp-muted, #5c6169);
                white-space: nowrap;
                max-width: 7rem;
            }
            .label-pill .pill-dot {
                width: 6px;
                height: 6px;
                border-radius: 50%;
                background: var(--pill-color, #94a3b8);
                flex-shrink: 0;
            }
            .label-pill .pi {
                font-size: 9px;
                flex-shrink: 0;
                color: var(--pill-color, #94a3b8);
            }
            .label-pill .pill-label {
                overflow: hidden;
                text-overflow: ellipsis;
            }
            .assignees {
                display: inline-flex;
                margin-left: auto;
            }
            .assignees .avatar + .avatar {
                margin-left: -4px;
            }
            .avatar {
                width: 16px;
                height: 16px;
                border-radius: 999px;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                font-size: 8px;
                font-weight: 600;
                color: #fff;
                background: var(--cmp-accent, #5e6ad2);
            }
            @keyframes toast-in {
                from {
                    opacity: 0;
                    transform: translateY(0.65rem) scale(0.98);
                }
                to {
                    opacity: 1;
                    transform: translateY(0) scale(1);
                }
            }
        `
    ]
})
export class NotificationToastHostComponent {
    readonly toastService = inject(NotificationToastService);
    readonly orgUsers = inject(OrgUsersService);
    private readonly productUi = inject(ProductUiService);
    private readonly orgService = inject(OrgService);
    private readonly notificationApi = inject(NotificationApiService);
    private readonly notificationInbox = inject(NotificationInboxService);

    private readonly identifierOpts = computed(() => ({
        projectKeyById: new Map(
            this.productUi.projects().map((p) => [p._id!, p.key ?? null])
        ),
        orgPostKeyPrefix: orgPostKeyPrefix(this.orgService.activeOrg())
    }));

    postId(item: NotificationToastView): string | null {
        const post = item.post;
        if (!post) return null;
        return formatPostIdentifier(post, this.identifierOpts());
    }

    headline(item: NotificationToastView): string {
        return item.post?.title || item.notification.title || '';
    }

    notificationText(item: NotificationToastView): string {
        return (item.notification.text || item.notification.message || '').trim();
    }

    assigneeId(a: unknown): string {
        if (typeof a === 'string') return a;
        if (a && typeof a === 'object') {
            const o = a as { _id?: string; id?: string };
            return String(o._id ?? o.id ?? '');
        }
        return String(a ?? '');
    }

    async onToastClick(item: NotificationToastView): Promise<void> {
        this.toastService.dismiss(item.toastId);

        const postId =
            item.post?._id ||
            (item.notification.referenceType === 'post' ? item.notification.referenceId : null);
        if (postId) {
            this.productUi.openPost(postId);
        }

        const notificationId = item.notification._id;
        if (notificationId && !item.notification.readAt) {
            try {
                await firstValueFrom(this.notificationApi.markRead(notificationId));
                this.notificationInbox.onMarkedRead(item.notification);
            } catch {
                /* ignore */
            }
        }
    }
}
